import type { ItemChange } from "../data/store.js";
import type { Anchors, Item, List, ListKind } from "../lib/api.js";
import { el, moveKeepingFocus, setText } from "../lib/dom.js";
import { todayIso } from "../lib/format.js";
import { icon } from "../lib/icons.js";
import { hasNote, paintNotePreview } from "../lib/markdown.js";
import { visibleStep } from "../lib/reorder.js";
import { categoryGroup, compareForDisplay, displayOrder } from "../../api/src/shared/list-order.js";
import { reorderController } from "./reorder.js";
import { reorderMode } from "./reorderMode.js";
import { MOVE_UNDO, moveLink, moveTarget, type MoveResult, type MoveTarget } from "./moveUndo.js";
import { feedbackLine, holdingPlace, type FeedbackLine } from "./feedback.js";
import { back, navigate, type Route } from "../lib/router.js";
import { openAddItem, type AddPosition } from "./add.js";
import {
  allDoneMessage,
  completedControl,
  hiddenCountMessage,
  revealedMessage,
  SHOWING_COMPLETED,
  tickedHiddenMessage,
  toggleCompleted,
} from "./completed.js";
import { DISCARD_EDITS, editorPanel, readOnlyDetails } from "./editor.js";
import { listActions } from "./listActions.js";
import { ORDER_BY, orderByAction } from "./orderBy.js";
import { openShareItem } from "./share.js";
import { remainingPhrase, uncheckedByList } from "../lib/counts.js";
import { openActivity } from "./activity.js";
import { SEPARATOR, actionMenu, actionsButton, type MenuItem } from "./menu.js";
import { ownerPolicy, type HeldWrite, type ListPolicy } from "./listPolicy.js";

import { confirmDeleteList } from "./deleteList.js";
export { deleteListMessage } from "./deleteList.js";

/** The question when another device deletes the row under a changed draft. Exported so the tests pin the words. */
export const ITEM_GONE = {
  title: "This item was deleted",
  message: "It was removed on another device. Your unsaved edits are still here to copy.",
};

/**
 * Said when a drag is cancelled because the list moved under it (spec §8, O04).
 * Exported so the tests pin the words.
 */
export const LIST_CHANGED = "The list changed. Reorder cancelled.";

/** What a list with nothing in it says (g56): its name, and where to add -- unless nobody here can (g72). */
export function emptyListMessage(name: string, canAdd = true): string {
  return canAdd ? `Nothing in ${name} yet. Add the first item below.` : `Nothing in ${name} yet.`;
}

/** Details for a view link's visitor (g72): the name of the panel, never "Edit". */
export const VIEW_ITEM = "Item details";
import {
  DELETE_ITEM,
  ROW_GONE,
  keepFocusIn,
  addRow,
  addedBy,
  attributionLine,
  deleteTitle,
  banner,
  editorGroup,
  inlineText,
  messageOf,
  noteOpener,
  paintMeta,
  rowPresentation,
  unreachable,
  type InlineText,
  type Screen,
  type ScreenAction,
  type ScreenHost,
  type Shell,
} from "./ui.js";

/**
 * The id of the description that tells a screen reader the title renames.
 *
 * A constant because two nodes have to agree on it, and one screen is mounted
 * at a time — the shell replaces the workspace's children rather than stacking
 * them, so this cannot collide with a second copy of itself.
 */
const RENAME_HINT = "list-rename-hint";

/**
 * One list and everything in it, read from the local mirror.
 *
 * The kind decides what is *offered*, never what is kept — see
 * `offersQuantity` in `views/ui.ts`.
 *
 * Every write goes to the store and comes back as a change event, which is the
 * only thing that touches the DOM. A local tick and an arriving delta therefore
 * travel exactly the same path, and there is no second code path to keep in
 * step with the first.
 */

/**
 * A row, and the way to bring it up to date without rebuilding it.
 *
 * `update` mutates in place on purpose. Replacing the node would destroy the
 * checkbox the finger is still on, and the next tap would land on whatever slid
 * into its place.
 */
interface Row {
  node: HTMLElement;
  update(item: Item): void;
  reorderable(enabled: boolean): void;
  /** True while the row's inline editor holds typed changes. */
  dirty(): boolean;
  /**
   * The item is gone and the row stays only for the draft in its editor:
   * see `orphan` on `inlineText`. It stops being a row of the list.
   */
  orphan(onDiscard: () => void): void;
}

/** What the watchers outside `build` need to reach once a list is on screen. */
interface View {
  busy(): boolean;
  header(list: List): void;
  item(change: ItemChange): void;
  /** The row the URL says is selected, or none. */
  select(itemId: string | undefined): void;
  /** Back from the row's note page (g77): the focus on its menu button. */
  returnTo(itemId: string): void;
  /** What the shell's plus does here, and where it sits beside the heading. */
  action(): ScreenAction | undefined;
  /** True while a row's editor, or the heading's, holds typed changes. */
  dirty(): boolean;
  /** A creator's name arrived or changed (g72). */
  creator(id: string): void;
  /**
   * Lets go of the listeners a drag needs on the document.
   *
   * They cannot live on the handle: a pointer leaves it the instant the row
   * moves and the finger does not, so the moves and the release arrive
   * somewhere else entirely.
   */
  release(): void;
}

/**
 * The owner's list, in the account's shell; or, with a policy, a list drawn
 * on some other frame -- a shared list's own page (g72).
 */
export function listScreen(shell: Shell, listId: string): Screen;
export function listScreen(shell: ScreenHost, listId: string, policy: ListPolicy): Screen;
export function listScreen(shell: ScreenHost, listId: string, given?: ListPolicy): Screen {
  const policy = given ?? ownerPolicy(shell as Shell, listId);
  const { store } = shell;
  const node = el("section", { class: "screen" });

  let view: View | undefined;
  // A list that was on screen and is not any more was deleted under the person
  // looking at it; one that was never there is a bad link. Two different things
  // to say, and only this tells them apart.
  let seen = false;
  // Except when they deleted it themselves and are already on their way out.
  let leaving = false;
  // What the URL currently selects. The screen is not rebuilt when this moves,
  // which is the whole reason the selection is a route rather than a remount.
  let selected: string | undefined;

  function render(): void {
    const list = store.list(listId);

    if (list) {
      if (!view) {
        seen = true;
        view = build(list);
        // A deep link straight to an item opens with its panel already up.
        view.select(selected);
        // There is a heading to put the plus beside now.
        shell.refreshAction();
      }
      return;
    }

    // Released before it is dropped, not left to `dispose`: once `view` is
    // undefined there is nothing left holding the document listeners, and a
    // list deleted on another device takes this path without the screen going
    // anywhere.
    view?.release();
    view = undefined;
    shell.detail.clear();
    node.replaceChildren(...absent());
    // And nothing to add to: the plus goes with the heading it sat beside.
    shell.refreshAction();
    // The focus was in what just went — a row, its menu, the question over
    // it — and the release put it back on a row that has gone with the
    // rest: it lands on the heading that took their place, not on the body.
    // Not when it is somewhere that stands, such as the Add popup's field.
    const focused = document.activeElement;
    if (!focused || focused === document.body || !focused.isConnected) {
      const heading = node.querySelector<HTMLElement>("h1");
      if (heading) {
        heading.setAttribute("tabindex", "-1");
        heading.focus({ preventScroll: true });
      }
    }
  }

  function absent(): Node[] {
    if (seen) {
      return [
        el("h1", { text: "This list was deleted" }),
        el("p", { class: "muted", text: "It was removed on this device or another one." }),
      ];
    }
    // Every branch carries a heading, including these two. They are the states
    // a deep link lands in before the first sync, and the screen had no `h1` at
    // all in them — which is the same hole the loaded screen had, in the states
    // the sweep does not visit because it only ever sees a seeded list.
    if (store.status() === "loading") {
      return [
        el("h1", { class: "visually-hidden", text: "Loading list" }),
        el("p", { class: "muted", text: "Loading…" }),
      ];
    }
    if (store.status() === "unreachable") {
      // Not "no such list": this device has downloaded nothing yet, so it has
      // no idea whether the list exists, and saying it does not would be a
      // guess dressed up as a fact.
      return [
        el("h1", { class: "visually-hidden", text: "List not downloaded yet" }),
        unreachable(store),
      ];
    }
    return [
      el("h1", { text: "No such list" }),
      el("p", { class: "muted", text: "It may have been deleted on another device." }),
    ];
  }

  function build(loaded: List): View {
    const status = el("div", { class: "status" });
    const setStatus = (child: Node | undefined) => {
      status.replaceChildren(...(child ? [child] : []));
    };
    /**
     * Shown in the slot, and said once in the shell's live region.
     *
     * Both, because they answer different people: the slot is what a sighted
     * person reads, and the region is the only thing a screen reader hears. The
     * slot cannot do the second job — it is hidden while empty, so it is outside
     * the accessibility tree exactly when a live region needs to be inside it.
     */
    const fail = (error: unknown) => {
      const message = messageOf(error);
      setStatus(banner(message));
      shell.announce(message);
    };
    // What the editor panel reports back with: an error to show, or nothing,
    // which is a write that took and a stale banner that should go with it.
    const onResult = (error?: unknown) => (error === undefined ? setStatus(undefined) : fail(error));

    const rows = el("ul", { class: "items" });
    const counts = el("span", { class: "list-counts" });
    /**
     * Where a reorder is said out loud.
     *
     * The repo had no live region before this: banners carry `role="status"`,
     * but they are replaced wholesale and say nothing about a move. Arrow keys
     * on the handle have to announce the new position, so one persistent polite
     * region lives here and only its text changes.
     */
    const announcer = el("p", { class: "visually-hidden", attrs: { role: "status" } });
    /**
     * Routine feedback (g102): a line under the header that goes after a few
     * seconds, beside the status slot rather than in it, so it neither covers
     * an error nor is wiped by one clearing. While Reorder mode is on, the
     * bar says it instead: the header may be a long scroll away.
     */
    const feedback: FeedbackLine = feedbackLine({ around: change => holdingPlace(feedback.node, rows, change) });
    const say = (message: string) => {
      // One message on the screen: entering the mode clears this line, and
      // leaving it clears the bar's.
      if (mode.active()) mode.say(message);
      else feedback.show(message);
    };

    const nodes = new Map<string, Row>();
    /** What each row's place depended on when it was last drawn: see `placeKey` and `item`. */
    const drawn = new Map<string, string>();

    let shownKind: ListKind = loaded.kind;
    // What the URL selects, and what the panel is actually showing. They differ
    // for exactly as long as a deep link is waiting for its first sync.
    let selectedId: string | undefined;
    let shownId: string | undefined;
    // Whether *this screen* pushed the selection currently in the URL. It did
    // not when the person arrived on the item's own URL, and calling back then
    // would take them out of the app rather than closing the panel.
    let pushed = false;
    // The row's actions menu (g18): one for the screen, opened beside
    // whichever ellipsis was pressed. Appended to the screen, never to a row
    // — see `views/menu.ts` for why.
    const menu = actionMenu();
    // One open editor at a time on this screen, heading included — E04's
    // contract, held by `editorGroup`.
    // Opening any of them ends the phone's Reorder mode first (g53).
    const editors = editorGroup({ opening: () => mode.exit() });
    // Whether this list shows its completed rows: a device view preference,
    // this account's and this list's (C02). Read through `shows` and never
    // copied, so a toggle is one write and every path sees it.
    const completed = policy.completed;
    // A completed row a deep link asked for while the filter hides it: drawn
    // for this visit, and the preference left alone (C05). Cleared by the
    // next toggle, or when the row stops being done.
    let revealed: string | undefined;
    // The row that is being ticked right now, so `item` can tell this
    // device's tick from another's: the first is announced, the second is
    // not. Set around `patchItem`, whose change event arrives synchronously.
    let ticking: string | undefined;
    // The row this screen is deleting from its menu, so `item` can tell its
    // own delete from another device's: the question already named the
    // draft it discards (D09), so the panel comes down without asking again.
    let deleting: string | undefined;
    /** Where the deep-link explanation goes. Not `status`: every write that takes clears that. */
    const notice = el("div", { class: "status list-notice" });

    // The store is the list, so the screen never keeps its own copy to go stale.
    const current = (): List => store.list(listId) ?? loaded;

    // ---- the order the rows are drawn in (g81) ------------------------------
    //
    // While the list's Order by category is on, the rows are drawn by category
    // and, inside one, in the normal order. It is a view: the store keeps the
    // normal order, every neighbour a write names comes from it, and switching
    // the setting off draws that order again, untouched. The comparison is the
    // shared one, so every device and the server agree on it.

    /** Whether the rows are drawn by category now. Read, never copied: the owner can switch it elsewhere. */
    const byCategory = (): boolean => current().orderBy === "category";
    /** The list's items in the order they are drawn, hidden ones included. */
    const shownOrder = (): Item[] => displayOrder(current().kind, current().orderBy, store.items(listId));
    const groupOf = (item: Item | undefined): string => categoryGroup(current().kind, item?.category);
    /** What a row's place depends on: its key, and its category while the list is drawn by one. */
    const placeKey = (item: Item): string => (byCategory() ? `${groupOf(item)}\n${item.orderKey}` : item.orderKey);
    /**
     * Every row of this row's category, drawn or not: what a move is held
     * inside while the list is drawn by category. Undefined when nothing
     * bounds a move.
     */
    function categoryOf(id: string): ReadonlySet<string> | undefined {
      if (!byCategory()) return undefined;
      const group = groupOf(store.item(id));
      return new Set(store.items(listId).filter((row) => groupOf(row) === group).map((row) => row.id));
    }
    let shownOrderBy = loaded.orderBy ?? null;

    /** Who is looking, when rows say who added them (g72); see `ListPolicy`. */
    const attribution = () => policy.attribution(current());
    let attributing = attribution() !== undefined;
    /** Whose rows went unlabelled at the last paint (g72). */
    let paintedViewer = attribution()?.viewer;
    /** Whether the rows offer changes now (g72). Read, never copied: it can end while on screen. */
    const editable = (): boolean => policy.controls() === "edit";
    const { gate } = policy;
    /** Asks for a name, and runs the change after (g72). See `WriteGate`. */
    const hold = (write: HeldWrite): void => gate.hold(write);

    // ---- the list's own header -------------------------------------------

    /**
     * The name, said once: it is the heading *and* the way to rename.
     *
     * It used to be two nodes — a hidden `h1` beside a visible `<input>` —
     * because an `h1` wrapped around a text field is a heading with no text in
     * it, and this screen had no heading at all before that. H01 asks for the
     * title to be a real heading, so the input goes and the same in-place
     * editor the rows use takes its place.
     *
     * Which reopens that hole from the other side for as long as the editor is
     * up, and `shadowName` is the answer: empty and hidden while the button is
     * showing, filled while the field is, so the heading is named in both
     * states and never carries the name twice.
     */
    const shadowName = el("span", { class: "visually-hidden", attrs: { hidden: true } });

    /**
     * Outside the heading, deliberately.
     *
     * A description names the affordance without joining the heading's own
     * accessible name — which H01 requires to be the list's name and nothing
     * else. Inside the `h1`, the heading would start reading "Errands Rename
     * list", and the rename button with it.
     */
    const renameHint = el("span", {
      class: "visually-hidden",
      attrs: { id: RENAME_HINT },
      text: "Rename list",
    });

    const titleText: Pick<InlineText, "node" | "show" | "finish" | "pending" | "dirty"> = !policy.owner ? staticTitle(loaded.name) : inlineText({
      text: loaded.name,
      // The name and nothing more. `Edit ${text}` is right for a row, where the
      // button is one control among several; here the button *is* the heading,
      // so whatever is added to it is added to the heading.
      label: (name) => name,
      describedBy: RENAME_HINT,
      // What the API takes for a list name. The row's 1000 would be a refusal
      // arriving at the end of the typing rather than the start.
      maxLength: 200,
      // Both opt-ins, and both are behaviour this screen already had before the
      // title became a button: the old `change` handler fired on blur, and an
      // emptied name put the old one back without sending anything.
      commitOnBlur: true,
      emptyRestores: true,
      // No Save and Cancel under the heading: H01 fixed its ways out as Enter,
      // Escape and blur, and with `commitOnBlur` a Save button could never be
      // reached — the click aimed at it would blur and save the field first.
      keysOnly: true,
      group: editors,
      commit(next) {
        try {
          return titleSave = store.patchList(listId, { name: next }).then(saved => {
            if (saved) setStatus(undefined);
            else {
              const message = "Could not save the list name on this device. Your draft is still here.";
              setStatus(banner(message));
              shell.announce(message);
            }
            return saved;
          });
        } catch (error: unknown) {
          // Kept open with the draft in it, the same as a row: a refusal is
          // made worse by also throwing away what was typed.
          fail(error);
          return false;
        }
      },
      onToggle(open) {
        shadowName.hidden = !open;
        // Emptied rather than merely hidden: `textContent` does not care about
        // CSS or the `hidden` attribute, so a name left sitting in here would
        // be read twice by anything walking the text — the suite included.
        shadowName.textContent = open ? current().name : "";
      },
    });

    const heading = el("h1", { class: "list-title", children: [shadowName, titleText.node] });
    // Beside the title on a shared page (g72): its "View only" or "Can edit".

    // The kind is not in the header any more (g52). It is set about once in
    // a list's life, stays in Edit list, and the rail and the overview still
    // draw its glyph; a pill here cost the summary line a third of its width
    // on a phone and told nobody anything they did not already know.

    const openOptions = el("button", {
      class: "list-options-open",
      attrs: { type: "button", "aria-label": "List options", title: "List options", "aria-haspopup": "menu", "aria-expanded": "false" },
      children: [icon("more")],
      // Reorder items first on a phone (g53), and every list action after it
      // opens a surface, so each ends the mode before it does. Order by
      // category (g81) joins Reorder's group on every kind of list, and opens
      // nothing, so the mode stays on through it.
      on: { click: () => menu.open(openOptions, (): MenuItem[] => {
        // Someone who may only read has the policy's few entries and nothing to order (g137).
        if (!editable()) return policy.viewMenu?.() ?? [];
        // Undo move (g102) is here only while there is a drop to undo, and
        // opens nothing, so it leaves the mode on as well.
        const undo: MenuItem[] = undoTarget.get() ? [{ label: MOVE_UNDO.label, icon: "undo", onSelect: undoMove }] : [];
        const ordering: MenuItem[] = [...mode.entry().filter(entry => entry !== SEPARATOR), ...(orderBy?.entries() ?? []), ...undo];
        // A link's editor gets the list's items and nothing of the list's own
        // (g72): who they are, then Clear completed.
        const rest = mode.leavingAll(policy.owner ? listOptions.entries() : [...(policy.menu?.() ?? []), SEPARATOR, gatedClear()]);
        return ordering.length ? [...ordering, SEPARATOR, ...rest] : rest;
      }) },
    });
    // Nothing in it for a view link (g72): no menu at all, unless the policy
    // gives a reader one (g137).
    const viewMenu = (): boolean => (policy.viewMenu?.().length ?? 0) > 0;
    openOptions.hidden = !editable() && !viewMenu();
    let clearingItems = false;
    /** Clear completed, asked for a name first when a link's editor has none (g72). */
    const gatedClear = (): MenuItem => {
      const entry = listOptions.clearEntry();
      return { ...entry, onSelect: () => {
        if (gate.ready()) { entry.onSelect(); return; }
        hold({ run: entry.onSelect, valid: () => store.items(listId).some(row => row.done), returnFocusTo: () => openOptions });
      } };
    };
    const shareList = (trigger: HTMLElement) => {
      if (editors.resolve()) policy.share?.(trigger);
    };
    let listQuestion: { close(): void } | undefined;
    const listOptions = listActions({
      shell, listId, trigger: openOptions,
      problem: message => setStatus(banner(message)),
      settled: () => { if (menu.isOpen()) menu.refresh(); },
      prepareEdit: () => titleText.finish(),
      share: () => shareList(openOptions),
      dirtyItem: id => (shownId === id && shell.detail.dirty()) || (nodes.get(id)?.dirty() ?? false),
      clear: ids => {
        clearingItems = true;
        try { return store.clearCompleted(listId, ids); }
        finally { clearingItems = false; }
      },
      afterClear: ids => {
        if (selectedId && ids.has(selectedId) && !store.item(selectedId)) {
          closing = true;
          pushed = false;
          navigate(policy.paths.list(), { replace: true, local: true });
        }
      },
      deleteList: () => {
        if (titleText.pending()) {
          shell.announce("The list name is still saving. Try deleting the list once it finishes.");
          return;
        }
        listQuestion = confirmDeleteList(shell, listId, {
          dirty: shell.detail.dirty() || titleText.dirty() || [...nodes.values()].some(row => row.dirty()) || orphans.size > 0,
          cancel: () => { listQuestion = undefined; },
          remove: () => {
            listQuestion = undefined;
            leaving = true;
            try { store.removeList(listId); shell.detail.clear(); navigate("/"); }
            catch (error) { leaving = false; fail(error); }
          },
          returnFocusTo: openOptions,
        });
      },
    });


    // ---- adding an item ---------------------------------------------------

    /**
     * Where the plus sits from 768 up: between the title and List options.
     *
     * The shell owns the button and moves it here; this screen only says
     * where. Below 768 it floats above the bottom navigation and the slot is
     * empty, which the stylesheet folds away so the title row pays no gap.
     */
    const actionSlot = el("div", { class: "list-action-slot" });

    /**
     * The popup, for the plus and for Insert below alike.
     *
     * The inline composer used to live here — one form, always on screen,
     * aimed at a row through a line above it. The popup replaces it (I01): a
     * surface opened on purpose, closed on success, and the one place that
     * knows how to make an item. The row it makes is revealed where it landed
     * rather than reordered to where the eye is (I04).
     */
    function openAdd(position?: AddPosition, returnFocusTo?: HTMLElement): void {
      mode.exit();
      openAddItem(shell, listId, position, {
        gate,
        returnFocusTo,
        onAdded: (row) => nodes.get(row.id)?.node.scrollIntoView?.({ block: "nearest" }),
      });
    }
    const addButton = addRow("Add a new item to the list", button => openAdd(undefined, button));
    addButton.hidden = !editable();
    const reordering = reorderController({
      root: node, rows, footer: addButton, nodeFor: id => nodes.get(id)?.node,
      all: () => store.items(listId), fallback: () => openOptions, announce,
      changedMessage: LIST_CHANGED, move: commitMove, obstacles: () => [mode.bar],
      group: categoryOf,
    });
    const mode = reorderMode({
      shell, rows, noun: "items", opener: openOptions, reordering,
      prepare: () => editors.resolve(),
      refreshMenu: () => { if (menu.isOpen()) menu.refresh(); },
      undo: () => undoMove(),
      entered: () => feedback.clear(),
    });
    /** The drop being saved, which becomes Undo move's once this device holds it. */
    let savingDrop: MoveTarget | undefined;
    const undoTarget = moveTarget(target => {
      mode.setUndo(target !== undefined);
      if (menu.isOpen()) menu.refresh();
    });

    /**
     * Order by category (g81), the owner's alone. Switching it on for a
     * Shopping list fills empty categories from saved text, so a draft is
     * asked about rather than saved behind the person's back: Save and order
     * commits it first and Keep editing leaves both alone. An editor nobody
     * has typed into just closes. The Details panel is not asked about: its
     * row moves and its draft stays where it is.
     */
    // The name's save in flight. Its editor saves on blur, and opening the
    // menu blurs it, so the switch waits for the save and then orders. A
    // failed save keeps the editor open with the draft, and nothing orders.
    let titleSave: Promise<boolean> | undefined;
    const afterTitle = (proceed: () => void) =>
      void titleSave?.then(() => { if (!editors.editing()) proceed(); });
    const orderBy = !policy.owner ? undefined : orderByAction({
      shell, listId, say,
      cancelDrag: () => reordering.cancel(),
      prepare: proceed => {
        reordering.cancel();
        if (titleText.pending()) { afterTitle(proceed); return; }
        const drafting = titleText.dirty() || [...nodes.values()].some(row => row.dirty());
        if (!drafting) { if (editors.resolve()) proceed(); return; }
        const field = () => node.querySelector<HTMLElement>("textarea.item-text-input") ?? undefined;
        shell.confirm({
          title: ORDER_BY.draftTitle,
          message: ORDER_BY.draftMessage,
          keep: { label: ORDER_BY.draftKeep, onChoose: () => undefined },
          act: { label: ORDER_BY.draftAct, onChoose: () => {
            if (editors.resolve()) proceed();
            // The list name saves asynchronously, and its editor stays open
            // until the write is durable: order once it has closed.
            else if (titleText.pending()) afterTitle(proceed);
          } },
        }, { returnFocusTo: () => field() ?? openOptions });
      },
      apply: applyOrder,
      problem: message => setStatus(banner(message)),
      refreshMenu: () => { if (menu.isOpen()) menu.refresh(); },
    });

    /**
     * A list-wide reorder, painted once. Switching Order by category on
     * fills categories on many rows in one burst, and relocating each one as
     * its event arrives, with the counts re-read after every one, costs
     * seconds on a long list. While the switch runs the changes are gathered
     * and the rows are put in the list's order in one pass. The page does not
     * scroll with them: moving a row drops it as Chrome's scroll anchor, and
     * `order-by-category.mjs` measures that the view stays put, from the
     * header and from far down the list.
     */
    let ordering = 0;
    let orderMoved = false;
    let orderTail = false;
    let orderFlush = false;
    function deferOrder(moved: boolean): void {
      if (moved) orderMoved = true;
      else orderTail = true;
      if (orderFlush) return;
      orderFlush = true;
      queueMicrotask(flushOrder);
    }
    function flushOrder(): void {
      orderFlush = false;
      const moved = orderMoved;
      const tail = orderTail || moved;
      orderMoved = orderTail = false;
      if (moved) arrange();
      if (!tail) return;
      afterChange();
      if (menu.isOpen()) menu.refresh();
    }
    /**
     * Puts every drawn row where the list's order now says, in one pass.
     *
     * A row already in its place is left where it is, and Chrome would hold
     * the page to such a row as its scroll anchor while the rows above it
     * change places, carrying a reader far down the list off with it. So the
     * list is no anchor until the pass has been laid out, and the page stays
     * where it was whichever rows moved.
     */
    let unanchored = 0;
    function arrange(): void {
      rows.style.setProperty("overflow-anchor", "none");
      cancelAnimationFrame(unanchored);
      // Two frames: the first runs before the layout the pass causes.
      unanchored = requestAnimationFrame(() => {
        unanchored = requestAnimationFrame(() => rows.style.removeProperty("overflow-anchor"));
      });
      let previous: Element | null = null;
      for (const item of shownOrder()) {
        const row = nodes.get(item.id);
        if (!row) continue;
        const target: Element | null = previous ? previous.nextElementSibling : rows.firstElementChild;
        if (target !== row.node) moveKeepingFocus(rows, row.node, target);
        drawn.set(item.id, placeKey(item));
        previous = row.node;
      }
      refreshHandles();
    }
    async function applyOrder<R>(command: () => Promise<R>): Promise<R> {
      ordering++;
      try {
        const pending = command();
        // The store has already moved the rows by now; they are drawn at once.
        flushOrder();
        return await pending;
      } finally {
        ordering--;
        flushOrder();
      }
    }


    // ---- rows -------------------------------------------------------------

    /**
     * What is left, directly under the title.
     *
     * H02 put the numbers a person asks for where they can be read without
     * scrolling. g52 cut them to one: "N open · N done" repeated the completed
     * count the Show/Hide completed control already carries, so the summary is
     * what remains, counted by the rule every badge uses (`uncheckedByList`).
     * A repeating item rolls its due date when ticked and stays unchecked, so
     * it keeps counting, as it does in the rail. No ring and no percentage: on
     * a list of four things a progress ring dominates what it describes.
     */
    function renderCounts(): void {
      const all = store.items(listId);
      // An empty list says nothing here: the add row below names the action,
      // and "0 remaining" over no rows would read as "all done".
      counts.textContent = all.length
        ? remainingPhrase(uncheckedByList(all).get(listId) ?? 0)
        : "";
    }

    // ---- completed-item visibility (C01-C05) ------------------------------

    function doneCount(): number {
      return store.items(listId).filter((item) => item.done).length;
    }

    /**
     * A row the filter leaves alone even though it is done (C04).
     *
     * The row the panel is showing: its panel comes down first (through the
     * guard when it holds a draft), and the row goes when `select(undefined)`
     * runs the sweep below — never out from under an open form. A row whose
     * inline editor holds typed changes: it stays until the draft is saved or
     * cancelled, and `onToggle` sweeps it then. And the row a deep link
     * revealed, for this visit.
     */
    function sticky(id: string): boolean {
      return shownId === id || revealed === id || (nodes.get(id)?.dirty() ?? false);
    }

    /** Whether the filter lets this row be drawn (C03). */
    function shows(item: Item): boolean {
      return !completed.hidden || !item.done || sticky(item.id);
    }

    /**
     * Takes rows off the screen, and moves the focus on if it was in one.
     *
     * C04's order: the next visible checkbox, then the previous, then the
     * visibility control — read *before* anything is removed, and skipping
     * rows that are leaving in the same pass. The checkbox and not Details,
     * because the finger that ticked a row is on a checkbox and the next
     * thing it wants is the next one.
     */
    function removeRows(ids: readonly string[]): void {
      const going = new Set(ids);
      const focused = ids.map((id) => nodes.get(id)?.node).find((row) => row?.contains(document.activeElement));
      // The nearest row past `from` that this batch leaves standing, and
      // the control in it to land on.
      const beyond = (from: Element | null, way: "nextElementSibling" | "previousElementSibling", control_: string) => {
        let at = from;
        while (at instanceof HTMLElement && going.has(at.dataset["id"] ?? "")) at = at[way];
        return at instanceof HTMLElement ? (at.querySelector<HTMLElement>(control_) ?? undefined) : undefined;
      };
      // The menu is open on a row that is going -- ticked away under the
      // filter, say: read where its focus lands before the row is gone, the
      // way a deleted row's is, and past any neighbour going with it.
      const anchor = menu.anchor();
      const menuRow = anchor === undefined ? undefined : ids.map((id) => nodes.get(id)?.node).find((row) => row?.contains(anchor));
      const menuLanding = menuRow
        ? (beyond(menuRow.nextElementSibling, "nextElementSibling", ".item-actions") ??
          beyond(menuRow.previousElementSibling, "previousElementSibling", ".item-actions") ??
          openOptions)
        : undefined;

      let landing: HTMLElement | undefined;
      if (focused) {
        // The same control on the row that stays: the ellipsis when the
        // focus was on the ellipsis (D09), the handle in Reorder mode, else
        // the checkbox (C04).
        const same = document.activeElement?.closest(".item-actions")
          ? ".item-actions"
          : document.activeElement?.closest(".item-handle") ? ".item-handle" : ".check input";
        landing =
          beyond(focused.nextElementSibling, "nextElementSibling", same) ??
          beyond(focused.previousElementSibling, "previousElementSibling", same) ??
          // Rows leave here only while the filter is on, and an active
          // filter's control is always offered (g52), so this is on screen.
          control.node;
      }

      for (const id of ids) {
        nodes.get(id)?.node.remove();
        nodes.delete(id);
        drawn.delete(id);
      }
      refreshHandles();
      if (menuLanding) {
        menu.close();
        menuLanding.focus({ preventScroll: true });
      } else {
        landing?.focus();
        // A menu open on a row that stays: its ends may have changed with
        // the rows around it, and it re-reads them. A no-op while closed.
        menu.refresh();
      }
    }

    /** Hide mode: every drawn row the filter no longer admits goes. */
    function sweep(): void {
      const gone = [...nodes.keys()].filter((id) => {
        const item = store.item(id);
        return item !== undefined && !shows(item);
      });
      if (gone.length) removeRows(gone);
      paintAllDone();
    }

    /**
     * Show mode: every row the filter was holding back is drawn again, where
     * its key says — `place` walks the rendered rows, which is what puts a
     * restored row back between its original neighbours (C03).
     */
    function restore(): void {
      for (const item of shownOrder()) {
        if (!nodes.has(item.id) && shows(item)) place(item.id, itemRow(item), false);
      }
      refreshHandles();
      paintAllDone();
    }

    function paintControl(): void {
      control.paint(completed.hidden, doneCount());
    }

    /**
     * C05: everything done and hidden is not an empty list. The invitation
     * stays tied to a list with nothing in it, and this line takes the rows'
     * place; the header's Show action is the one way back, so there is no
     * second button here.
     */
    function paintAllDone(): void {
      const all = store.items(listId);
      const allHidden = completed.hidden && all.length > 0 && rows.children.length === 0;
      allDone.hidden = !allHidden;
      allDone.textContent = allHidden ? allDoneMessage(doneCount()) : "";
      // A list with nothing in it at all (g56) names itself and points at the
      // add row below. Never while everything is only hidden: that is the
      // line above, and "nothing in" it would read as data gone.
      const name = store.list(listId)?.name ?? "";
      const nothing = all.length === 0 && name !== "";
      emptyLine.hidden = !nothing;
      setText(emptyLine, nothing ? emptyListMessage(name, editable()) : "");
    }

    /** Whether the row the panel shows would leave the view once the filter goes on. */
    function selectedWouldHide(): boolean {
      return shownId !== undefined && !completed.hidden && (store.item(shownId)?.done ?? false);
    }

    function clearNotice(): void {
      revealed = undefined;
      notice.replaceChildren();
    }

    /** The preference flips; nothing is written to the store (C02, C03). */
    function apply(): void {
      // A drag in flight would be re-parenting rows the sweep is removing.
      reordering.cancel();
      // A revealed row was for this visit; a deliberate toggle ends it.
      clearNotice();
      completed.set(!completed.hidden);
      if (completed.hidden) sweep();
      else restore();
      paintControl();
      // The rows the menu's Move up and Move down walk are changing under
      // it; it is rebuilt from the ellipsis, not patched.
      menu.close();
      shell.announce(completed.hidden ? hiddenCountMessage(doneCount()) : SHOWING_COMPLETED);
    }

    const control = completedControl(() =>
      toggleCompleted({ editors, detail: shell.detail, selectedWouldHide, dismiss, apply }),
    );
    const allDone = el("p", { class: "muted list-all-done", attrs: { hidden: true } });
    const emptyLine = el("p", { class: "muted list-empty", attrs: { hidden: true } });

    /**
     * Puts a row where the list's order says, among the rows already drawn.
     *
     * This used to hunt for the run of completed rows at the bottom and drop
     * new items above it, because ticking something sank it. Nothing sinks now,
     * so position is the order alone: the key, and the category first while
     * the list is drawn by one (g81). The comparison is against the *rendered*
     * rows rather than a fresh `store.items`, so a row being edited or one the
     * finger is on keeps its place in the walk.
     *
     * `settle` is false for a caller placing many rows, which settles the
     * handles once after the last.
     */
    function place(id: string, row: Row, settle = true): void {
      const me = store.item(id);
      let before: Element | null = null;

      if (me !== undefined) {
        const compare = compareForDisplay(current().kind, current().orderBy);
        // A row that belongs after the last one drawn is appended without the
        // walk, which is every row of a list being drawn in order.
        const last = rows.lastElementChild instanceof HTMLElement ? rows.lastElementChild.dataset["id"] : undefined;
        const tail = last === undefined || last === id ? undefined : store.item(last);
        if (!tail || compare(tail, me) >= 0) {
          for (const node of rows.children) {
            const at = node instanceof HTMLElement ? node.dataset["id"] : undefined;
            // The row itself is passed over: it is not before or after itself.
            const other = at === undefined || at === id ? undefined : store.item(at);
            if (other !== undefined && compare(me, other) < 0) {
              before = node;
              break;
            }
          }
        }
      }

      moveKeepingFocus(rows, row.node, before);
      nodes.set(id, row);
      // What this row was drawn at, so `item` can tell a move from an edit.
      drawn.set(id, me ? placeKey(me) : "");
      if (settle) refreshHandles(row);
    }

    /**
     * Availability belongs to the visible list, not to the last row painted.
     * A handle works when there is another drawn row to move among: any other
     * row, or while the list is drawn by category (g81), another row of its
     * own category, since a move cannot leave one.
     */
    let handlesEnabled: boolean | undefined = false;
    function refreshHandles(added?: Row): void {
      if (byCategory()) {
        const sizes = new Map<string, number>();
        const groups = new Map<string, string>();
        for (const id of nodes.keys()) {
          const group = groupOf(store.item(id));
          groups.set(id, group);
          sizes.set(group, (sizes.get(group) ?? 0) + 1);
        }
        for (const [id, row] of nodes) row.reorderable((sizes.get(groups.get(id)!) ?? 0) >= 2);
        // Each row has its own answer now, so the next plain refresh repaints them all.
        handlesEnabled = undefined;
        return;
      }
      const enabled = nodes.size >= 2;
      if (enabled !== handlesEnabled) {
        handlesEnabled = enabled;
        for (const row of nodes.values()) row.reorderable(enabled);
      } else {
        added?.reorderable(enabled);
      }
    }

    /**
     * Moves a row that is already drawn to where its key now says.
     *
     * Only ever called for the row that was just moved deliberately. Re-placing
     * on every change would yank rows out from under a finger mid-scroll, which
     * is the behaviour the sink was removed for.
     */
    /**
     * Rows kept only for a draft whose item was deleted elsewhere (g54). Not
     * in `nodes`: they are no longer rows of the list, and nothing that walks
     * the list should find them. They are drafts all the same, so leaving the
     * screen asks while one is up.
     */
    const orphans = new Set<HTMLElement>();
    function keepOrphan(row: Row): void {
      orphans.add(row.node);
      row.orphan(() => {
        // Cancel: the draft goes, and the focus with it to the next row's
        // checkbox, the one before, or the add row -- where a finger that
        // was working down the list goes next. Past any other kept row,
        // whose checkbox is withdrawn.
        const near = (step: (node: Element) => Element | null) => {
          for (let node = step(row.node); node; node = step(node)) {
            if (!node.classList.contains("gone")) return node.querySelector<HTMLElement>(".check input") ?? undefined;
          }
          return undefined;
        };
        const landing = near((node) => node.nextElementSibling) ?? near((node) => node.previousElementSibling) ?? addButton;
        orphans.delete(row.node);
        row.node.remove();
        landing.focus({ preventScroll: true });
      });
    }

    function relocate(id: string): void {
      const row = nodes.get(id);
      if (!row) return;
      // Moved in place rather than taken out and put back, so an edit open
      // in it keeps its caret (g54). The walk in `place` passes over the row
      // itself: its own key is never greater than its key.
      place(id, row);
    }

    // ---- reordering -------------------------------------------------------

    /** The rows as drawn, in order. */
    function renderedIds(): { id: string }[] {
      return [...rows.children].flatMap((node) => {
        const id = node instanceof HTMLElement ? node.dataset["id"] : undefined;
        return id === undefined ? [] : [{ id }];
      });
    }

    /**
     * The drawn rows a step of this row walks among: all of them, or while the
     * list is drawn by category (g81) only its own category's, so Move up on a
     * category's first row and Move down on its last are not offered, as they
     * are not at the ends of a list.
     */
    function stepRows(id: string): { id: string }[] {
      const shown = renderedIds();
      if (!byCategory()) return shown;
      const group = groupOf(store.item(id));
      return shown.filter((row) => groupOf(store.item(row.id)) === group);
    }

    /**
     * Where a row sits now, as the pair of neighbours that would put it back.
     *
     * Read *before* a move, so undo has somewhere to aim. Against the full
     * list, not the rendered rows: with completed rows hidden the row's real
     * neighbours may be off screen, and an undo aimed at the visible pair
     * would put it back on the wrong side of a hidden one. Before a move the
     * store's order is the screen's order less the hidden rows, so nothing
     * the person is looking at is lost by asking the store.
     */
    function currentAnchors(id: string): Anchors | undefined {
      const siblings = store.items(listId);
      const at = siblings.findIndex((row) => row.id === id);
      if (at < 0) return undefined;
      return { afterId: siblings[at - 1]?.id ?? null, beforeId: siblings[at + 1]?.id ?? null };
    }

    function announce(message: string): void {
      // Cleared first: a screen reader says nothing when the text it is given
      // is the text already there, and moving a row twice to the same position
      // is an ordinary thing to do with the arrow keys.
      announcer.textContent = "";
      announcer.textContent = message;
    }

    function positionOf(id: string): string {
      // As drawn: the place a person sees the row at, by category or not.
      const siblings = shownOrder();
      const at = siblings.findIndex((row) => row.id === id);
      return at < 0 ? "" : `${at + 1} of ${siblings.length}`;
    }

    /**
     * The one place a reorder is written, from the handle, the arrow keys, the
     * panel and undo alike.
     *
     * `undoable` is false for a single step, and deliberately: a step is one
     * keystroke from undone by pressing the other arrow. A drop is the case
     * undo exists for, since it can land somewhere nobody intended. Either
     * way the move is now the latest one, so a step clears the drop Undo
     * move was holding rather than leave it undoing something older.
     *
     * It answers with how the move went (g102). `undo` is the move Undo
     * makes: it leaves the position unsaid, because its caller says "Move
     * undone." once the move is saved.
     */
    function commitMove(id: string, anchors: Anchors, options: { undoable?: boolean; undo?: boolean; reverses?: string } = {}): MoveResult {
      if (!gate.ready()) {
        // Put back where the store has it, then ask (g72). The move runs once
        // named, if the row and both neighbours it named are still here.
        relocate(id);
        const row = nodes.get(id)?.node;
        const control = row && (mode.control(row) ?? row.querySelector<HTMLElement>(".item-actions") ?? undefined);
        hold({
          run: () => void commitMove(id, anchors, options),
          valid: () => {
            const here = (other: string | null | undefined) => !other || store.item(other)?.listId === listId;
            if (store.item(id)?.listId === listId && here(anchors.afterId) && here(anchors.beforeId)) return true;
            announce(LIST_CHANGED);
            return false;
          },
          returnFocusTo: () => (control?.isConnected ? control : undefined),
        });
        return { status: "deferred" };
      }
      const back = currentAnchors(id);
      let saved: Promise<boolean> | undefined;
      // The id this move is queued under, told before `saved` answers (g108).
      let made: string | undefined;
      try {
        saved = store.moveItem(id, anchors, moveLink(options.reverses, (operationId) => { made = operationId; }));
      } catch (error: unknown) {
        fail(error);
        return { status: "refused" };
      }
      if (!saved) return { status: "missing" };
      relocate(id);
      setStatus(undefined);
      if (!options.undo) {
        const item = store.item(id);
        announce(item ? `Moved ${item.text} to ${positionOf(id)}.` : MOVE_UNDO.moved);
      }
      const target: MoveTarget | undefined = options.undoable && back ? { id, back } : undefined;
      // This is the latest move now, so the last drop is no longer Undo's. The
      // new one is offered, and said, once this device holds it: an undo
      // queued behind a save that then failed would report a move undone that
      // was never stored. A later move meanwhile leaves this one behind. A
      // drop that could not be written has the store's own notice.
      undoTarget.set(undefined);
      savingDrop = target;
      if (target) void saved.then((ok) => {
        if (savingDrop !== target) return;
        savingDrop = undefined;
        if (!ok || released) return;
        if (made !== undefined) target.operationId = made;
        undoTarget.set(target);
        say(MOVE_UNDO.moved);
      });
      return { status: "applied", saved };
    }

    /**
     * Undo move: puts the last dropped row back between the neighbours it
     * had. The only thing on this screen that offers to undo, from List
     * options and, where it fits, the Reorder bar.
     *
     * "Move undone." is said for a move that was made and saved, and for
     * nothing else: not one a link's visitor has yet to be named for, which
     * keeps its target and runs once they are, and not one that was refused.
     */
    function undoMove(): void {
      const target = undoTarget.get();
      if (!target) return;
      if (!gate.ready()) {
        hold({
          run: undoMove,
          valid: () => undoTarget.get() === target && store.item(target.id)?.listId === listId,
          returnFocusTo: () => (openOptions.isConnected ? openOptions : undefined),
        });
        return;
      }
      const result = commitMove(target.id, target.back, { undo: true, ...(target.operationId !== undefined ? { reverses: target.operationId } : {}) });
      if (result.status !== "applied") return;
      void result.saved.then((ok) => {
        if (!ok || released) return;
        say(MOVE_UNDO.undone);
        announce(MOVE_UNDO.undone);
      });
    }

    /**
     * Selecting a row is a navigation, and that is the point.
     *
     * The panel it opens is a bottom sheet on a phone, and back is how a sheet
     * is closed — so the selection has to be somewhere back can move through.
     * Docked, the panel is a column of the page that never opens or closes,
     * so a history entry per row would mean ten back presses to leave a list
     * after reading ten items: there the selection replaces instead.
     */
    function selectItem(id: string): void {
      // Another row over a changed draft is asked about first (E05). The
      // same row again is not a move, and `select` leaves the panel alone.
      if (shownId !== undefined && id !== shownId) {
        shell.detail.guarded(() => go(id));
        return;
      }
      go(id);
    }

    function go(id: string): void {
      // Replaced, not pushed, when a panel is already open: moving from one
      // row to the next is not a step back through, and stacking an entry per
      // row is what leaves Back pointing at a panel the person already closed.
      const replace = shell.detail.persistent() || selectedId !== undefined;
      navigate(policy.paths.item(id), { replace });
      if (!replace) pushed = true;
    }

    // The open panel, so a name that changes can be repainted in it (g72).
    let shownEditor: { repaint(): void } | undefined;

    // Where the focus goes when the selected row is deleted from its own
    // panel: the Details button that opened the panel goes with the row, so
    // this is captured just before the row is removed — the next row's
    // Details, else the previous row's, else List options (D09).
    let lostFocus: HTMLElement | undefined;

    /** The person closed the panel: undo the navigation that opened it. */
    let closing = false;
    function dismiss(): void {
      closing = true;
      if (pushed) {
        pushed = false;
        back();
        return;
      }
      navigate(policy.paths.list(), { replace: true, local: true });
    }

    /**
     * What the route asks for, applied.
     *
     * Called for every route that names this list, including the ones that
     * arrive by the back button — so it is the single place that knows which
     * row is selected, and the rows themselves only ever read it.
     */
    function select(itemId: string | undefined): void {
      // A new selection opens the panel, which is an edit surface (g53).
      if (itemId !== undefined && itemId !== selectedId) mode.exit();
      closing = false;
      const previous = selectedId;
      selectedId = itemId;
      if (previous !== undefined && previous !== itemId) markSelected(previous);

      if (itemId === undefined) {
        pushed = false;
        shownId = undefined;
        shownEditor = undefined;
        shell.detail.clear();
        // The row the panel was showing stayed on screen while it was up,
        // whatever the filter said (`sticky`). Now the panel is down and
        // `clear()` has handed the focus back to the row's Details, it
        // leaves — and the focus moves on with it.
        sweep();
        return;
      }

      // Already on screen. The panel is a form somebody may be halfway through,
      // and a route that did not actually move must not throw that away — which
      // it would, since selecting the row the inspector already shows is an
      // ordinary thing to do at the wide breakpoint.
      if (itemId === shownId) return;

      const item = store.item(itemId);
      if (!item || item.listId !== listId) {
        // An item this device has not downloaded yet is not the same as one
        // that does not exist: the first sync applies every list change before
        // any item change, so a fresh device reaches here with the row still
        // missing. The URL is left alone until the mirror is settled, and
        // `item` below retries when the row lands.
        if (!item && store.status() !== "ready") return;

        // A hand-written or stale link: either nothing has that id, or it
        // belongs to another list — and an editor for a row that is not in the
        // list under it would save and delete somewhere nobody can see.
        selectedId = undefined;
        shownId = undefined;
        shownEditor = undefined;
        shell.detail.clear();
        navigate(policy.paths.list(), { replace: true });
        return;
      }

      // A link to a completed row the filter is hiding (C05): drawn for this
      // visit, with a line saying why it is here, and the preference left as
      // it was — a link is not a decision about the list.
      if (!nodes.has(itemId)) {
        revealed = itemId;
        place(itemId, itemRow(item));
        notice.replaceChildren(el("p", { class: "muted", text: revealedMessage(item.text) }));
        shell.announce(revealedMessage(item.text));
        paintAllDone();
      }

      shownId = itemId;
      markSelected(itemId);
      // The row's ellipsis, whose menu's Edit opened the panel (g18), and
      // where the focus returns when it comes down. It used to be
      // `.item-body`, which is no longer a control at all.
      const actions = nodes.get(itemId)?.node.querySelector<HTMLElement>(".item-actions") ?? undefined;
      // The fields and nothing else (g18): the moves, the inserts and Delete
      // are the row's menu's, reached from the ellipsis without a form.
      const attributed = () => {
        const who = attribution();
        const latest = store.item(itemId);
        return who ? attributionLine(store, (latest ?? item).creatorId, who.viewer) : undefined;
      };
      if (!editable()) {
        // A view link's visitor reads the item and changes nothing (g72).
        const reading = readOnlyDetails(item, current(), {
          attribution: attributed,
          latest: () => {
            const now = store.item(itemId);
            return now ? { item: now, list: current() } : undefined;
          },
          onClose: () => shell.detail.dismiss(),
        });
        shownEditor = reading;
        shell.detail.present(reading.node, {
          label: `${VIEW_ITEM}: ${item.text}`,
          title: VIEW_ITEM,
          onDismiss: dismiss,
          returnFocus: () => {
            const text = nodes.get(itemId)?.node.querySelector<HTMLElement>(".item-text-button");
            return text?.isConnected ? text : lostFocus;
          },
        });
        return;
      }
      const editor = editorPanel(shell, item, current(), {
        gate,
        onDismiss: dismiss,
        onResult,
        attribution: attributed,
      });
      shownEditor = editor;
      shell.detail.present(editor.node, {
        label: `Edit ${item.text}`,
        title: "Edit item",
        onDismiss: dismiss,
        // Read when the panel comes down: the row may have gone by then.
        returnFocus: () => (actions?.isConnected ? actions : lostFocus),
        guard: editor.guard,
      });
    }

    /** Only the row that gained or lost the selection is touched. */
    function markSelected(id: string): void {
      const row = nodes.get(id)?.node;
      row?.classList.toggle("selected", id === selectedId);
    }

    /** The row's "Added by" name, or undefined when it says nothing (g72). */
    function attributedTo(row: Item): string | undefined {
      const who = attribution();
      return who ? addedBy(store, row.creatorId, who.viewer) : undefined;
    }

    function itemRow(item: Item): Row {
      // The row's own copy of what it is showing, so a handler fired after a
      // change acts on the current values rather than the ones it was built on.
      let shown = item;
      /** Its inline save, once it may run: see `commit` below. */
      const saveText = (next: string): boolean => {
        try {
          store.patchItem(shown.id, { text: next });
          setStatus(undefined);
          return true;
        } catch (error: unknown) {
          // Kept open with the draft in it: the write was refused, and
          // throwing away what was typed is the one response that makes a
          // refusal worse than it already is.
          fail(error);
          return false;
        }
      };

      const check = el("input", { attrs: { type: "checkbox", checked: item.done } });
      const itemText = inlineText({
        text: item.text,
        // The quantity where the button shows it, so the name holds the
        // visible words a voice command would say (g58). The checkbox and
        // the ellipsis stay named for the text alone: the quantity is said
        // once per row.
        label: (text, quantity) => (quantity ? `Edit ${quantity} ${text}` : `Edit ${text}`),
        commit(next) {
          if (gate.ready()) return saveText(next);
          // A link's editor with no name yet (g72): the field holds its draft
          // read-only while the name is asked, and saves once it is given --
          // or stays open with the draft when the prompt is cancelled.
          const id = shown.id;
          const field = (): HTMLElement | undefined => row.querySelector<HTMLElement>("textarea.item-text-input") ?? undefined;
          return new Promise<boolean>((resolve) => hold({
            run: () => resolve(saveText(next)),
            valid: () => store.item(id)?.listId === listId,
            cancelled: () => resolve(false),
            returnFocusTo: field,
          }));
        },
        // A view link's row reads its item rather than editing it (g72).
        readOnly: {
          active: () => !editable(),
          label: (text, quantity) => (quantity ? `Details for ${quantity} ${text}` : `Details for ${text}`),
          activate: () => selectItem(shown.id),
        },
        onToggle: (open) => {
          // At the wide breakpoint the panel stays on screen beside a live row,
          // so opening an editor here would point two editors at one item —
          // the Details fault from the other direction.
          if (open && selectedId === shown.id) dismiss();
          paint(shown);
          // The draft that was keeping a done row on screen is saved or
          // dropped: the row goes now (C04). `close` has already handed the
          // focus back to the row's text, so the focus rule moves it on.
          if (!open && !shows(shown)) {
            removeRows([shown.id]);
            paintAllDone();
          }
        },
        // A blank draft's refusal, said through the app's one live region as
        // well as beside the field — see `announce` on `inlineText`.
        announce: (message) => shell.announce(message),
        group: editors,
      });
      const meta = el("span", { class: "item-meta" });
      const note = el("span", { class: "item-note" });

      // The note page (g77), from View note in the menu or a tap on the
      // preview (g139): the owner's only, so a link's visitor has neither.
      // Whichever row is editing is resolved first, as Edit does, and Reorder
      // mode ends, as it does for any surface.
      const view = policy.paths.view;
      const viewNote = view && mode.leaving(() => {
        if (editors.resolve()) navigate(view(shown.id), { opener: true });
      });
      if (viewNote) noteOpener(note, () => { if (hasNote(shown.note)) viewNote(); });

      /**
       * The six-dot handle: drag it, or focus it and use the arrow keys.
       *
       * It replaces the ↑ ↓ pair rather than joining it. `DESIGN-SYSTEM.md:161`
       * keeps Move up and Move down as the explicit alternative, and they
       * live in the row's actions menu now, so nothing is lost for anyone who
       * cannot drag. The spec's R01 then took the `+` off the row as well,
       * and every other action is in the menu. A phone row draws no handle
       * outside Reorder mode (g53); wider layouts show it beside the checkbox.
       *
       * `icon()` marks its SVG `aria-hidden`, so the name has to be here.
       */
      const handle = el("button", {
        class: "item-handle",
        children: [icon("grip")],
        attrs: { type: "button", title: "Reorder" },
      });
      // The one overflow entry point: the ellipsis opens the actions menu
      // (g18). The row body used to be one big button that opened the
      // panel, which the design system rules out twice: text starts an edit,
      // and empty space does nothing. Four columns — handle, checkbox,
      // content, actions — and nothing else.
      const actions = actionsButton();
      actions.addEventListener("click", () => menu.open(actions, entries));

      /**
       * What the menu offers for this row, read when it opens and again on
       * `refresh`: the inserts around it, the moves, Edit, and Delete, in
       * four groups. Move up and Move down at the visible ends are offered
       * disabled rather than missing, so they can be heard.
       */
      const entries = (): MenuItem[] => {
        const all = store.items(listId);
        const shownRows = stepRows(shown.id);
        // Both neighbours named now, from the full list: the row on the far
        // side is what keeps the position meaningful if this one is deleted
        // while the text is being typed. Above the first row is the pair the
        // selector shows as Beginning.
        const insert = (where: "above" | "below"): void => {
          const rows = store.items(listId);
          const at = rows.findIndex((row) => row.id === shown.id);
          const position =
            where === "above"
              ? { afterId: rows[at - 1]?.id ?? null, beforeId: shown.id }
              : { afterId: shown.id, beforeId: rows[at + 1]?.id ?? null };
          openAdd(position, actions);
        };
        // A step moves the row node, and the focus with it: put back on the
        // ellipsis, as the handle's arrow keys put it back on the handle.
        const move = (by: -1 | 1): void => {
          step(by);
          actions.focus({ preventScroll: true });
        };
        const share: MenuItem[] = [SEPARATOR, { label: "Share item", icon: "share", onSelect: mode.leaving(() => {
          if (editors.resolve()) openShareItem(shell, listId, shown.id, actions);
        }) }];
        return [
          { label: "Insert below", icon: "insertBelow", onSelect: () => insert("below") },
          { label: "Insert above", icon: "insertAbove", onSelect: () => insert("above") },
          SEPARATOR,
          {
            label: "Move up",
            icon: "up",
            disabled: visibleStep(all, shownRows, shown.id, -1) === undefined,
            onSelect: () => move(-1),
          },
          {
            label: "Move down",
            icon: "down",
            disabled: visibleStep(all, shownRows, shown.id, 1) === undefined,
            onSelect: () => move(1),
          },
          SEPARATOR,
          // Only with a note to read, and only on the owner's own list (g77).
          ...(viewNote && hasNote(shown.note) ? [{
            label: "View note",
            icon: "note" as const,
            onSelect: viewNote,
          }] : []),
          // History is the owner's (g110), and only while the server lets it be read.
          ...(policy.owner && store.activityAvailable() ? [{
            label: "Activity",
            icon: "history" as const,
            onSelect: mode.leaving(() => { if (editors.resolve()) openActivity(shown.listId, shown.id); }),
          }] : []),
          {
            label: "Edit",
            icon: "edit",
            onSelect: mode.leaving(() => {
              // The panel is a second editor, so whichever row is editing is
              // resolved first — this one or another. Leaving an editor open
              // lets the panel save and a later Enter in the forgotten
              // textarea overwrite it with a stale draft. A refusal keeps
              // that editor open, and going anywhere then costs the person
              // their draft — so this stops instead.
              if (editors.resolve()) selectItem(shown.id);
            }),
          },
          // Sharing is the owner's (g72).
          ...(policy.owner ? share : []),
          SEPARATOR,
          { label: "Delete", icon: "trash", danger: true, onSelect: mode.leaving(() => askDelete(shown.id)) },
        ];
      };

      const row = el("li", {
        attrs: { "data-id": item.id },
        children: [
          handle,
          el("label", { class: "check", children: [check] }),
          el("div", { class: "item-body", children: [itemText.node, meta, note] }),
          actions,
        ],
      });

      /**
       * One step, named as the pair of neighbours it lands between.
       *
       * The arithmetic is in `lib/reorder.ts` because the actions menu offers
       * the same two moves and the two must agree.
       */
      function step(by: -1 | 1): void {
        const anchors = visibleStep(store.items(listId), stepRows(shown.id), shown.id, by);
        // At the end of the list, or of the row's category. Nothing to send: a mutation that moves
        // nothing still bumps `updated_at` and pushes a row into every other
        // device's delta.
        if (!anchors) return;
        commitMove(shown.id, anchors);
      }

      handle.addEventListener("keydown", (event: KeyboardEvent) => {
        // The press focuses this button, so the arrows are reachable mid-drag.
        // A step then would write a move under a gesture that is about to
        // write another, and `listChanged` would read it as the list moving.
        // Prevented as well as ignored: the browser's own answer to an arrow
        // on a focused button is to scroll the page, which would move the
        // rows under a pointer that has not moved and leave the marker stale.
        if (reordering.active()) {
          if (event.key === "ArrowUp" || event.key === "ArrowDown") event.preventDefault();
          return;
        }
        // Arrow keys on the focused handle, which is what the design system
        // asks for alongside the drag: `:161`. Prevented, or the page scrolls
        // under the row that just moved.
        if (event.key === "ArrowUp") {
          event.preventDefault();
          step(-1);
          handle.focus();
        } else if (event.key === "ArrowDown") {
          event.preventDefault();
          step(1);
          handle.focus();
        }
      });

      reordering.bind(handle, () => shown.id);

      const paint = (next: Item) => {
        shown = next;

        // Assigned whole rather than toggled, so the selected class has to be
        // part of it: `className` is what would otherwise quietly drop it on
        // the next tick of the row that is open in the panel.
        const look = rowPresentation(next, {
          // The list as the store has it now, not `shownKind`, which only
          // moves when the heading hears of the change.
          kind: current().kind,
          today: todayIso(),
          selected: next.id === selectedId,
          editing: itemText.editing(),
          addedBy: attributedTo(next),
        });
        row.className = look.className;
        // Re-applied, because `className` was just assigned whole. A delta
        // arriving mid-gesture would otherwise drop the lifted row back to
        // looking untouched while its ghost carried on moving — the same trap
        // `editing` hit, in the one place it lasts for a gesture rather than
        // until the next repaint.
        if (reordering.dragging(next.id)) row.classList.add("dragging");

        check.checked = next.done;
        // A view link ticks nothing (g72): the box says what is done, and is not a control.
        check.disabled = !editable() || Boolean(next.repeat && next.due && next.due.date > todayIso());
        handle.hidden = !editable();
        actions.hidden = !editable();
        check.setAttribute(
          "aria-label",
          next.done ? `Mark ${next.text} not done` : `Mark ${next.text} done`,
        );

        // Writes the button, never the open field, so a delta for this row
        // arriving mid-edit leaves a half-typed line alone; the button, with
        // any new quantity, comes back when the editor closes.
        itemText.show(next.text, look.prefix);

        paintMeta(meta, look.metaParts);

        // The note without its markdown (g76): a row shows words, never markup.
        paintNotePreview(note, next.note);

        // Named per row rather than once: "Reorder" alone is one identical
        // button per row to anything reading the list aloud.
        handle.setAttribute("aria-label", `Reorder ${next.text}`);
        actions.setAttribute("aria-label", `Actions for ${next.text}`);

      };

      /** The tick, once it may run. A repeating item completes its occurrence; nothing else is. */
      const tick = (done: boolean): void => {
        ticking = shown.id;
        try {
          if (shown.repeat) store.completeItem(shown.id);
          else store.patchItem(shown.id, { done });
          setStatus(undefined);
        } catch (error: unknown) {
          check.checked = shown.done;
          fail(error);
        } finally {
          ticking = undefined;
        }
      };

      check.addEventListener("change", () => {
        // The tick is already flipped; the store's change event paints the rest
        // of the row around it. Nothing waits for the server — that is the
        // whole point of the queue — and nothing rebuilds the list, so the
        // control under the finger survives being used.
        const want = check.checked;
        if (gate.ready()) {
          tick(want);
          return;
        }
        // No name yet (g72): the box goes back to what is stored until the
        // name is given, and the tick runs then, if it still means the same.
        check.checked = shown.done;
        const id = shown.id;
        const due = shown.due?.date;
        hold({
          run: () => { check.checked = want; tick(want); },
          valid: () => {
            const latest = store.item(id);
            if (!latest || latest.listId !== listId) return false;
            // Someone else completed this occurrence meanwhile: running it
            // again would skip the next one.
            return latest.repeat ? latest.due?.date === due : latest.done !== want;
          },
          returnFocusTo: () => (check.isConnected ? check : undefined),
        });
      });

      paint(item);
      return {
        node: row,
        update: paint,
        // Keep the handle's footprint and focus target when no move is possible,
        // wherever the handles show: from 768 up, and in Reorder mode (g53).
        reorderable: enabled => handle.setAttribute("aria-disabled", String(!enabled)),
        dirty: () => itemText.dirty(),
        orphan(onDiscard) {
          // Out of the list's reckoning -- the walk that places rows, the
          // drag, the counts all go by `data-id` -- and nothing on it to
          // press but its own Cancel.
          delete row.dataset["id"];
          row.classList.add("gone");
          check.disabled = true;
          handle.hidden = true;
          actions.hidden = true;
          itemText.orphan(ROW_GONE, onDiscard);
        },
      };
    }

    // ---- what the store tells this screen ---------------------------------

    function header(next: List): void {
      // The name lands without a guard, and that is not an oversight: the
      // draft lives in the textarea and `show` writes to the button, which is
      // a different node — the finding already recorded on `show` in `ui.ts`,
      // where an early return added to "protect the draft" was measured and
      // changed no behaviour at all. The `kind` control below holds both its
      // value and the person's hand, so it still needs one.
      titleText.show(next.name);
      // A link turned on or off: every row gains or loses its "Added by"
      // (g72), and so does the open panel.
      const nowAttributing = attribution() !== undefined;
      if (nowAttributing !== attributing) {
        attributing = nowAttributing;
        repaintRows();
        shownEditor?.repaint();
      }
      // The empty line names the list, so a rename repaints it.
      paintAllDone();
      // Only while the editor is up — closed, it is empty on purpose.
      if (!shadowName.hidden) shadowName.textContent = next.name;
      listOptions.refresh();
      if (menu.isOpen()) menu.refresh();

      // Order by category switched on or off (g81), here or by the owner
      // elsewhere, or the kind changed under it, which changes whether an
      // aisle's name leads: every row is put where the order now says. No
      // key changed, so this is the one place that sees it.
      const nextOrderBy = next.orderBy ?? null;
      if (nextOrderBy !== shownOrderBy || (nextOrderBy !== null && next.kind !== shownKind)) {
        shownOrderBy = nextOrderBy;
        // A drag in flight was aimed at gaps that no longer exist.
        reordering.cancel();
        arrange();
      }

      if (next.kind !== shownKind) {
        shownKind = next.kind;
        // A Shopping row leads with its quantity and no other kind's does
        // (g58), so every row repaints for its text and meta line. Not behind
        // the panel's guard below: a row's text is not a form, and an open
        // row editor is untouched by it -- the draft is in its field.
        repaintRows();
        // The kind decides which fields the editor offers, so an open one is
        // redrawn — and only then, since a redraw costs whatever was typed.
        //
        // `shownId` is cleared first, and it has to be: `select` returns early
        // when the route has not moved, which is right on every other path —
        // a panel is a form somebody may be halfway through — and is exactly
        // the path a kind change arrives on. Without this the call did
        // nothing, and a to-do list turned into a shopping list kept offering
        // the due date and hiding the quantity until the panel was reopened.
        if (selectedId !== undefined && !shell.detail.dirty()) {
          shownId = undefined;
          select(selectedId);
        }
      }
    }

    function item(change: ItemChange): void {
      const existing = nodes.get(change.id);
      // The open panel's facts, when someone else changed its item (g72). An
      // edit form repaints only who added it; its fields are the person's.
      if (change.item && change.id === selectedId) shownEditor?.repaint();

      // Where the menu's focus goes if this is the row it is open on.
      let lostAnchor: HTMLElement | undefined;
      if (!change.item) {
        if (selectedId === change.id && existing) {
          const neighbour = existing.node.nextElementSibling ?? existing.node.previousElementSibling;
          lostFocus = neighbour?.querySelector<HTMLElement>(".item-actions") ?? openOptions;
        }
        const anchor = menu.anchor();
        if (existing && anchor && existing.node.contains(anchor)) lostAnchor = focusAfterLoss(change.id)();
        // The row holds the focus itself -- on the ellipsis the previous
        // delete landed it on, when two neighbours go in one delta: it
        // moves on again rather than falling to the document with the row.
        const holding = existing?.node.contains(document.activeElement) ? focusAfterLoss(change.id) : undefined;
        // Another device deleted the row being typed into (g54). The draft
        // stays where it is for copying, as the panel's does (E05); their
        // own Delete, a list going and Clear completed each let it go.
        if (existing?.dirty() && deleting !== change.id && !leaving && !clearingItems) {
          if (lostAnchor) menu.close();
          keepOrphan(existing);
          keepFocusIn(existing.node);
        } else {
          existing?.node.remove();
          holding?.().focus({ preventScroll: true });
        }
        nodes.delete(change.id);
        drawn.delete(change.id);
        // The drop Undo move held was this row's.
        if (undoTarget.get()?.id === change.id) undoTarget.set(undefined);
        if (savingDrop?.id === change.id) savingDrop = undefined;
        refreshHandles();
        if (revealed === change.id) clearNotice();
        // The Delete question was about this row and another device got
        // there first: closed, rather than left naming a row that is gone
        // or stacked over the draft question below. After the row's removal,
        // so that its return focus lands on the neighbour.
        if (asked?.id === change.id) {
          const { close } = asked;
          asked = undefined;
          close();
        }
        // The panel was showing a row that no longer exists — theirs to delete
        // or another device's. Either way the URL still names it, so the
        // selection is dropped rather than left pointing at nothing. Not
        // before asking about a changed draft, though (E05): a delete on
        // another device must not take what was typed here with it. The
        // question says why, because Keep editing on a row that is gone is
        // for copying the text, not for saving it. Their own Delete asks
        // nothing: the editor lets go of its draft before removing the row.
        // Nor does a list they are deleting: its rows go as orphans while the
        // screen is on its way out, and a question then would be a question
        // over a list that no longer exists.
        if (selectedId === change.id && !leaving && !clearingItems) {
          if (deleting !== change.id && shell.detail.dirty()) {
            shell.detail.confirm({
              ...ITEM_GONE,
              keep: { label: DISCARD_EDITS.keep, onChoose: () => shell.detail.resume() },
              act: { label: DISCARD_EDITS.discard, onChoose: dismiss },
            });
          } else {
            dismiss();
          }
        }
      } else if (existing) {
        // The row, not the panel. A panel is a form full of what someone is
        // still typing and a delta must not take that away from them.
        existing.update(change.item);
        // A revealed row that is not done any more is simply a row.
        if (revealed === change.id && !change.item.done) clearNotice();
        if (!shows(change.item)) {
          // Ticked with the filter on: the row leaves after the local update,
          // which is C04's one exception to a row staying where it was
          // ticked. Not a reorder — its key is untouched and Show puts it
          // back between the same neighbours. Said only for this device's
          // own tick: a row another device ticked goes the way a row it
          // deleted does.
          removeRows([change.id]);
          if (ticking === change.id) shell.announce(tickedHiddenMessage(change.item.text, doneCount()));
        } else if (completed.hidden && change.item.done && shownId === change.id && !closing) {
          // The row the panel shows was ticked with the filter on. It stays
          // while the panel is up (`sticky`), and the panel comes down —
          // after the draft question when it holds one (C04, C05) — with the
          // row leaving in the sweep `select(undefined)` runs.
          if (ticking === change.id) shell.announce(tickedHiddenMessage(change.item.text, doneCount()));
          shell.detail.guarded(dismiss);
        } else if (placeKey(change.item) !== drawn.get(change.id)) {
          // A changed order key is a move, and so is a changed category while
          // the list is drawn by category (g81). Nothing else is: an edit, a
          // tick and a due date all leave both alone. So this re-places
          // exactly the rows that moved and no others, which is the rule
          // `relocate` exists to keep — and it is what makes a move from the
          // Details panel land where it belongs instead of waiting for a
          // remount.
          //
          // A move made on another device lands here too, now that
          // `mirror.ts` compares on `orderKey` as well — before that, a
          // reorder-only delta was written to IndexedDB and reported to
          // nobody.
          if (ordering) deferOrder(true);
          else relocate(change.id);
        }
      } else if (shows(change.item)) {
        // A row this screen has not drawn: new, or a completed one the filter
        // was holding back that is not done any more.
        place(change.id, itemRow(change.item));
      }

      if (ordering) deferOrder(false);
      else afterChange();
      // The menu is open over a list that just changed: its row gone with
      // the menu still up — the focus goes where a deleted row's would — or
      // its ends and its place to re-read. Not when the row's own draft is
      // being asked about above: the question took the focus, and landing
      // on the neighbour would take it back off the question.
      if (menu.isOpen()) {
        const anchor = menu.anchor();
        if (anchor && !anchor.isConnected) {
          menu.close();
          if (!shell.detail.asking()) lostAnchor?.focus({ preventScroll: true });
        } else if (ordering) {
          deferOrder(false);
        } else {
          menu.refresh();
        }
      }
    }

    /** What every change re-reads: the counts, the controls and the drag's rows. */
    function afterChange(): void {
      renderCounts();
      paintControl();
      paintAllDone();
      reordering.changed();
      listOptions.refresh();
    }

    /**
     * Delete, from the row's menu (g18): the question stands alone in the
     * popup host, there being no panel for it to be a state of. Named as
     * last saved; the edits sentence when the row's own panel holds
     * edits -- only ever the inspector, since an undocked panel is modal
     * and the row's menu cannot open under it -- or when the row's inline
     * editor does, which is open at any width. Cancel, Escape, the
     * backdrop and Back all put the focus back on the ellipsis; a confirmed
     * delete puts it where a deleted row's goes (D09).
     */
    function askDelete(id: string): void {
      const target = store.item(id);
      if (!target) return;
      if (!gate.ready()) {
        // Named first (g72), and then asked as ever: the question re-reads
        // the row, so a delete that waited never runs unconfirmed.
        const ellipsis = nodes.get(id)?.node.querySelector<HTMLElement>(".item-actions") ?? undefined;
        hold({ run: () => askDelete(id), valid: () => store.item(id)?.listId === listId,
          returnFocusTo: () => (ellipsis?.isConnected ? ellipsis : undefined) });
        return;
      }
      const ellipsis = nodes.get(id)?.node.querySelector<HTMLElement>(".item-actions");
      const landing = focusAfterLoss(id);
      // The inspector's draft on this row, or the row's own editor's: either
      // goes with the row, and the question says so.
      const dirty = (shownId === id && shell.detail.dirty()) || (nodes.get(id)?.dirty() ?? false);
      const question = shell.confirm(
        {
          title: deleteTitle(target.text),
          message: dirty ? `${DELETE_ITEM.message} ${DELETE_ITEM.discards}` : DELETE_ITEM.message,
          keep: {
            label: "Cancel",
            onChoose: () => {
              asked = undefined;
            },
          },
          act: {
            label: "Delete item",
            danger: true,
            onChoose: () => {
              // Let go before the write, whose change event arrives
              // synchronously: this delete is not another device's.
              asked = undefined;
              removeNow(id);
            },
          },
        },
        { returnFocusTo: () => (ellipsis?.isConnected ? ellipsis : landing()) },
      );
      asked = { id, close: question.close };
    }

    /** The standalone Delete question while it stands, and the row it names. */
    let asked: { id: string; close(): void } | undefined;

    function removeNow(id: string): void {
      // Set around the write, whose change event arrives synchronously: it
      // is how `item` tells this delete from another device's.
      deleting = id;
      try {
        store.removeItem(id);
        setStatus(undefined);
      } catch (error: unknown) {
        fail(error);
      } finally {
        deleting = undefined;
      }
    }

    /**
     * Where the focus goes once this row is gone: the next row's ellipsis,
     * else the previous row's, else List options (D09). Captured now, read
     * later — either neighbour may have gone too by then.
     */
    function focusAfterLoss(id: string): () => HTMLElement {
      const row = nodes.get(id)?.node;
      // The handle in Reorder mode, when that is where the focus was (g53).
      const kind = row?.contains(document.activeElement) && document.activeElement?.closest(".item-handle")
        ? ".item-handle" : ".item-actions";
      const next = row?.nextElementSibling?.querySelector<HTMLElement>(kind);
      const previous = row?.previousElementSibling?.querySelector<HTMLElement>(kind);
      return () => [next, previous].find((candidate) => candidate?.isConnected) ?? openOptions;
    }

    // ---- assembly ---------------------------------------------------------

    for (const item of shownOrder()) if (shows(item)) place(item.id, itemRow(item), false);
    refreshHandles();
    renderCounts();
    paintControl();
    paintAllDone();

    /** Every row, from the store as it is now. */
    function repaintRows(): void {
      for (const [id, row] of nodes) {
        const latest = store.item(id);
        if (latest) row.update(latest);
      }
    }

    let calendarDay = todayIso();
    const refreshCalendar = () => {
      const nextDay = todayIso();
      if (nextDay === calendarDay) return;
      calendarDay = nextDay;
      repaintRows();
    };
    window.addEventListener("focus", refreshCalendar);
    document.addEventListener("visibilitychange", refreshCalendar);

    node.replaceChildren(
      el("header", {
        class: "list-head",
        children: [
          el("div", { class: "list-title-row", children: [heading, actionSlot, openOptions] }),
          ...(policy.byline ? [policy.byline] : []),
          el("p", { class: "list-meta", children: [counts, control.node] }),
        ],
      }),
      ...(policy.owner ? [renameHint] : []),
      status,
      feedback.node,
      notice,
      mode.bar,
      // The list and its add row, and one gap between them (g51). Everything
      // else in here is out of the flow -- hidden, visually hidden, fixed or a
      // closed popover -- so the add row sits one gap below the last row, the
      // all-done line, or the header when the list is empty.
      el("div", {
        class: "list-body",
        children: [rows, allDone, emptyLine, announcer, reordering.marker, menu.node, addButton],
      }),
    );

    let released = false;
    function release(): void {
      released = true;
      savingDrop = undefined;
      undoTarget.set(undefined);
      feedback.dispose();
      stopControls();
      orderBy?.dispose();
      window.removeEventListener("focus", refreshCalendar);
      document.removeEventListener("visibilitychange", refreshCalendar);
      reordering.dispose();
      mode.dispose();
      menu.close();
      // A list deleted on another device takes the `render()` path, which
      // releases the view and replaces the screen — leaving a modal in the top
      // layer over the "This list was deleted" message if this is not here.
      // The standalone Delete question too: the tombstone that would have
      // closed it arrives after the view is gone, to nobody.
      listOptions.dispose();
      listQuestion?.close();
      if (asked) {
        const { close } = asked;
        asked = undefined;
        close();
      }
    }

    // No plus for a view link (g72).
    const action = (): ScreenAction | undefined => (!editable() ? undefined : {
      label: "Add item",
      slot: actionSlot,
      activate: () => openAdd(),
    });

    /**
     * A link stopped working while the list was on screen (g72): every way to
     * change it goes. A draft already open stays, to copy; saving it now says
     * why it cannot.
     */
    const stopControls = policy.onControls(() => {
      // A page that moved to another visitor's copy has a new viewer, and
      // the copy's rows arrive only where they differ: the labels are
      // repainted here, or the visitor's own rows keep "Added by" them.
      const viewer = attribution()?.viewer;
      if (viewer !== paintedViewer) {
        paintedViewer = viewer;
        repaintRows();
        shownEditor?.repaint();
      }
      if (editable()) return;
      menu.close();
      mode.exit();
      reordering.cancel();
      openOptions.hidden = !viewMenu();
      addButton.hidden = true;
      savingDrop = undefined;
      undoTarget.set(undefined);
      feedback.clear();
      repaintRows();
      paintAllDone();
      shell.refreshAction();
    });

    // The heading is an editor too: a rename in progress is as much a draft
    // as a row's text.
    const dirty = (): boolean =>
      titleText.dirty() || [...nodes.values()].some((row) => row.dirty()) || orphans.size > 0;

    /** A creator's name changed (g72): the rows they added, and the open panel. */
    function creator(id: string): void {
      if (!attribution()) return;
      for (const [rowId, row] of nodes) {
        const latest = store.item(rowId);
        if (latest && latest.creatorId === id) row.update(latest);
      }
      shownEditor?.repaint();
    }

    const returnTo = (itemId: string): void => {
      nodes.get(itemId)?.node.querySelector<HTMLElement>(".item-actions")?.focus();
    };

    return { header, item, select, returnTo, action, dirty, busy: () => titleText.pending(), release, creator };
  }

  const stopLists = store.onLists((change) => {
    if (change.id !== listId || leaving) return;
    if (change.list && view) view.header(change.list);
    else render();
  });
  const stopItems = store.onItems(listId, (change) => view?.item(change));
  const stopCreators = store.onCreators((changed) => view?.creator(changed.id));
  // Only the screens that have nothing to show care about the status, and this
  // one has something as soon as the list arrives.
  // While this list is on screen, the store keeps it fresh if someone else
  // may be changing it (g71). Changes arrive through the watchers above, row
  // by row, so a draft being typed is never replaced.
  const stopWatching = store.watchList(listId);
  const stopStatus = store.onStatus(() => {
    if (!view) return render();
    // The mirror has settled. A deep-linked item that still is not here is a
    // wrong URL rather than an early one, and `select` says so now.
    if (selected !== undefined) view.select(selected);
  });

  render();

  return {
    node,

    // None until the list is on screen: there is nowhere to put it and
    // nothing to add to. The shell is told each time that changes.
    action: () => view?.action(),

    dirty: () => view?.dirty() ?? false,
    busy: () => view?.busy() ?? false,
    returnTo: (itemId) => view?.returnTo(itemId),

    /**
     * The same list, a different selection.
     *
     * The shell calls this instead of building the screen again, which is what
     * keeps the scroll position and every row's place while a panel opens
     * beside them — and what makes the back button close a sheet rather than
     * leave the list.
     */
    update(route: Route) {
      const selection = policy.paths.selection(route);
      if (!selection) return;
      selected = selection.itemId;
      view?.select(selected);
    },

    dispose() {
      stopLists();
      stopItems();
      stopCreators();
      stopStatus();
      stopWatching();
      // Navigation replaces the DOM; it does not cancel a listener on the
      // document. Five of them per visit to a list would outlive every screen
      // that ever drew one.
      view?.release();
      // The panel belongs to the shell, which outlives this screen: a list
      // left with one open would otherwise hand the next screen a form for an
      // item it knows nothing about.
      shell.detail.clear();
    },
  };
}

/**
 * The heading of a list that is not the looker's own (g72): its name, and
 * nothing to press. Renaming a list is its owner's.
 */
function staticTitle(name: string): Pick<InlineText, "node" | "show" | "finish" | "pending" | "dirty"> {
  const node = el("span", { class: "item-text list-title-text", text: name });
  return {
    node,
    show: (next) => setText(node, next),
    finish: () => true,
    pending: () => false,
    dirty: () => false,
  };
}
